import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Plus, Search, Mail, Phone, MapPin, Pencil, Trash2, Building, Upload, Loader2, Send, Linkedin } from 'lucide-react';
import { CopyEmail } from '@/components/CopyEmail';
import { toast } from 'sonner';
import { CountrySelect } from '@/components/CountrySelect';
import { CompanySelect } from '@/components/CompanySelect';
import { TherapeuticAreaSelect } from '@/components/TherapeuticAreaSelect';
import { DiseaseAreaSelect } from '@/components/DiseaseAreaSelect';
import BulkSendProgress from '@/components/BulkSendProgress';

import { Pagination } from '@/components/Pagination';

const API = '/api';
const COMPANY_TYPES = ['Big Pharma', 'Mid Pharma', 'CDMO'];

const emptyForm = {
  name: '', email: '', phone: '', designation: '', company_name: '',
  company_type: 'Big Pharma', location: '', department: '', interest_areas: [], disease_areas: {}, preferred_stages: [], notes: '',
};

export default function PharmaContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const searchTimeoutRef = useRef(null);
  const [companyFilter, setCompanyFilter] = useState('All');
  const [taFilter, setTaFilter] = useState('');
  const [interestAreas, setInterestAreas] = useState([]);
  const [uniqueContactsWithInterests, setUniqueContactsWithInterests] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Bulk email state
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkBody, setBulkBody] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkTaskId, setBulkTaskId] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [testSending, setTestSending] = useState(false);

  const fetchContacts = async (page = currentPage, searchQuery = search, taVal = taFilter) => {
    setLoading(true);
    try {
      // Scope to category=pharma so contacts attached to companies that have
      // been moved to Commercialization Partners (Medis → Marjeta, etc.)
      // don't bleed into this list — they live under the partner contacts tab.
      const params = new URLSearchParams({ page, limit: 500, category: 'pharma' });
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (taVal) params.set('therapeutic_area', taVal);
      const res = await axios.get(`${API}/pharma-contacts?${params}`, { withCredentials: true });
      const data = res.data;
      setContacts(data.items || data);
      setTotalPages(data.pages || 1);
      setTotalCount(data.total || 0);
      setCurrentPage(data.page || 1);
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchContacts(1, '');
    axios.get(`${API}/pharma-contacts/interest-areas`, { withCredentials: true })
      .then(({ data }) => {
        setInterestAreas(data.areas || []);
        setUniqueContactsWithInterests(data.unique_contacts || 0);
      })
      .catch(() => {});
  }, []);

  const handleBulkEmailSend = async () => {
    if (!bulkSubject.trim() || !bulkBody.trim()) {
      toast.error('Subject and email body are required');
      return;
    }
    setBulkSending(true);
    try {
      const res = await axios.post(`${API}/bulk-email/pharma-contacts`, {
        subject: bulkSubject,
        email_body: bulkBody,
      }, { withCredentials: true });
      setBulkTaskId(res.data.task_id);
      setBulkProgress({ total: res.data.total_recipients, sent: 0, failed: 0, status: 'running', skipped: res.data.skipped_bounced || 0 });
      toast.success(`Sending to ${res.data.total_recipients} contacts...`);
      pollBulkStatus(res.data.task_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start bulk email');
      setBulkSending(false);
    }
  };

  const handleTestEmail = async () => {
    if (!bulkSubject.trim() || !bulkBody.trim()) {
      toast.error('Subject and email body are required');
      return;
    }
    setTestSending(true);
    try {
      const res = await axios.post(`${API}/bulk-email/pharma-contacts/test`, {
        subject: bulkSubject,
        email_body: bulkBody,
      }, { withCredentials: true });
      toast.success(`Test email sent to ${res.data.to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestSending(false);
    }
  };

  const pollBulkStatus = (taskId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/bulk-email/status/${taskId}`, { withCredentials: true });
        setBulkProgress(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(interval);
          setBulkSending(false);
          if (res.data.status === 'completed') {
            toast.success(`Bulk email complete: ${res.data.sent} sent, ${res.data.failed} failed`);
          } else {
            toast.error('Bulk email task failed');
          }
        }
      } catch {
        clearInterval(interval);
        setBulkSending(false);
      }
    }, 2000);
  };


  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value);
      setCurrentPage(1);
      fetchContacts(1, value, taFilter);
    }, 400);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchContacts(newPage, search, taFilter);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/pharma-contacts/${editing}`, form, { withCredentials: true });
        toast.success('Contact updated');
      } else {
        await axios.post(`${API}/pharma-contacts`, form, { withCredentials: true });
        toast.success('Contact added');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      fetchContacts();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === 'suppressed_contact') {
        if (detail.status === 'MANUALLY_REMOVED') {
          if (window.confirm(`${detail.message}\nRemoved ${String(detail.suppressed_at || '').slice(0, 10)}${detail.reason ? ` — ${detail.reason}` : ''}.\n\nRestore this contact?`)) {
            try {
              await axios.post(`${API}/suppression/${detail.record_id}/restore`, { recreate_contact: true }, { withCredentials: true });
              toast.success('Contact restored from suppression registry');
              setDialogOpen(false); setEditing(null); setForm({ ...emptyForm }); fetchContacts();
            } catch (e2) { toast.error(e2?.response?.data?.detail || 'Restore failed'); }
          }
        } else {
          toast.error(`${detail.message} Restoring requires an admin override in Data Ops → Suppressed Contacts.`, { duration: 10000 });
        }
        return;
      }
      if (err?.response?.status === 409) {
        toast.error(detail || 'This contact already exists');
      } else {
        toast.error('Failed to save contact');
      }
    }
  };

  const handleEdit = (contact) => {
    // Ensure disease_areas defaults to {} for older contacts that don't yet
    // have the field, otherwise the DiseaseAreaSelect can't read indices.
    setForm({ ...contact, disease_areas: contact.disease_areas || {} });
    setEditing(contact.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/pharma-contacts/${id}`, { withCredentials: true });
      toast.success('Contact deleted');
      setDeleteTarget(null);
      fetchContacts();
    } catch { toast.error('Failed to delete'); setDeleteTarget(null); }
  };

  const uniqueCompanies = [...new Set(contacts.map(c => c.company_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const filtered = contacts.filter(c =>
    (companyFilter === 'All' || c.company_name === companyFilter)
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="pharma-contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pharma Contacts
          </h1>
          <p className="text-sm text-slate-500 mt-1">{totalCount} contact{totalCount !== 1 ? 's' : ''} in database</p>
          <p className="text-sm text-slate-500 mt-1">BD and licensing professionals at mid/big pharma</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setUploading(true);
              try {
                const formData = new FormData();
                formData.append('file', file);
                const res = await axios.post(`${API}/pharma-contacts/bulk-upload`, formData, {
                  withCredentials: true,
                  headers: { 'Content-Type': 'multipart/form-data' },
                });
                const { imported, updated, duplicates, skipped, invalid_emails } = res.data;
                const invalidCount = invalid_emails?.length || 0;
                const parts = [`${imported} added`];
                if (updated) parts.push(`${updated} updated (fuzzy-matched)`);
                if (duplicates) parts.push(`${duplicates} unchanged`);
                let msg = `Import complete: ${parts.join(', ')}`;
                if (invalidCount > 0) {
                  msg += `, ${invalidCount} rejected (invalid email)`;
                  const invalidList = invalid_emails.slice(0, 5).map(e => `${e.email} — ${e.reason}`).join('\n');
                  toast.error(`Rejected emails:\n${invalidList}${invalidCount > 5 ? `\n...and ${invalidCount - 5} more` : ''}`, { duration: 10000 });
                }
                if (skipped - invalidCount > 0) {
                  msg += `, ${skipped - invalidCount} rows without name/email`;
                }
                toast.success(msg);
                fetchContacts();
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Failed to upload file');
              } finally {
                setUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }
            }}
            data-testid="upload-pharma-file-input"
          />
          <Button
            variant="outline"
            className="border-sky-200 text-sky-700 hover:bg-sky-50"
            onClick={() => setBulkEmailOpen(true)}
            data-testid="bulk-email-pharma-btn"
          >
            <Send className="w-4 h-4 mr-2" /> Bulk Email
          </Button>
          <Button
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-pharma-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Importing...' : 'Upload Excel'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditing(null); setForm({ ...emptyForm }); } }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-md shadow-sm active:scale-95 transition-all" data-testid="add-pharma-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editing ? 'Edit Contact' : 'Add Pharma Contact'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required data-testid="input-pharma-name" />
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input value={form.designation} onChange={e => setForm(p => ({ ...p, designation: e.target.value }))} placeholder="e.g. VP Business Development" data-testid="input-pharma-designation" />
                </div>
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <CompanySelect value={form.company_name} onChange={v => setForm(p => ({ ...p, company_name: v }))} testId="input-pharma-company" />
                </div>
                <div className="space-y-2">
                  <Label>Company Type</Label>
                  <Select value={form.company_type} onValueChange={v => setForm(p => ({ ...p, company_type: v }))}>
                    <SelectTrigger data-testid="select-pharma-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COMPANY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="input-pharma-email" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="input-pharma-phone" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" /> LinkedIn URL</Label>
                  <Input
                    value={form.linkedin_url || ''}
                    onChange={e => setForm(p => ({ ...p, linkedin_url: e.target.value }))}
                    placeholder="https://www.linkedin.com/in/…"
                    data-testid="input-pharma-linkedin"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <CountrySelect value={form.location} onChange={v => setForm(p => ({ ...p, location: v }))} testId="input-pharma-location" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Therapeutic Areas</Label>
                <TherapeuticAreaSelect
                  value={form.interest_areas || []}
                  onChange={(v) => {
                    const removed = (form.interest_areas || []).filter(t => !v.includes(t));
                    let nextDA = { ...(form.disease_areas || {}) };
                    removed.forEach(t => { delete nextDA[t]; });
                    setForm(p => ({ ...p, interest_areas: v, disease_areas: nextDA }));
                  }}
                  testId="input-pharma-areas"
                />
                {(form.interest_areas || []).length > 0 && (
                  <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-slate-100">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">
                      Disease areas (optional)
                    </p>
                    {(form.interest_areas || []).map((ta) => (
                      <div key={ta} className="flex items-start gap-2">
                        <Badge variant="secondary" className="text-[11px] px-2 py-0.5 mt-0.5 shrink-0">{ta}</Badge>
                        <DiseaseAreaSelect
                          therapeuticArea={ta}
                          value={(form.disease_areas || {})[ta] || []}
                          onChange={(v) => setForm(p => ({
                            ...p,
                            disease_areas: { ...(p.disease_areas || {}), [ta]: v }
                          }))}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Preferred Stages</Label>
                <div className="flex flex-wrap gap-2">
                  {['Drug Discovery', 'Pre-Clinical', 'IND', 'Phase 1', 'Phase 2', 'Phase 3', 'Commercial'].map(stage => (
                    <button
                      key={stage}
                      type="button"
                      onClick={() => setForm(p => ({
                        ...p,
                        preferred_stages: (p.preferred_stages || []).includes(stage)
                          ? p.preferred_stages.filter(s => s !== stage)
                          : [...(p.preferred_stages || []), stage]
                      }))}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        (form.preferred_stages || []).includes(stage)
                          ? 'bg-sky-100 border-sky-300 text-sky-700 font-medium'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                      data-testid={`stage-toggle-${stage.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {stage}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="submit-pharma-btn">{editing ? 'Update' : 'Add Contact'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search by name, company, country, email..." value={searchInput} onChange={e => handleSearchChange(e.target.value)} data-testid="search-pharma" />
        </div>
        <Select value={companyFilter} onValueChange={v => { setCompanyFilter(v); }}>
          <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="company-filter">
            <SelectValue placeholder="Filter by company" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All Companies</SelectItem>
            {uniqueCompanies.map(name => (
              <SelectItem key={name} value={name}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={taFilter || 'all'} onValueChange={v => {
          const val = v === 'all' ? '' : v;
          setTaFilter(val);
          setCurrentPage(1);
          fetchContacts(1, search, val);
        }}>
          <SelectTrigger className="w-[220px] h-9 text-sm" data-testid="ta-filter">
            <SelectValue placeholder="Therapeutic Interest" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Interests</SelectItem>
            <SelectItem value="__HAS_INTERESTS__">Has Stated Interests ({uniqueContactsWithInterests} people)</SelectItem>
            {interestAreas.map(a => (
              <SelectItem key={a.area} value={a.area}>{a.area} ({a.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {taFilter && (
          <button onClick={() => { setTaFilter(''); fetchContacts(1, search, ''); }}
            className="text-xs text-slate-400 hover:text-slate-600 underline" data-testid="clear-ta-filter">Clear</button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="py-16 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">{search ? 'No contacts match' : 'No pharma contacts added yet'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="pharma-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Name</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Company</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Email</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Designation</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Phone</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} className="hover:bg-slate-50/50" data-testid={`pharma-row-${c.id}`}>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1.5">
                        <a
                          href={`/pharma/${c.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-semibold text-slate-900 hover:text-[#00709B] hover:underline inline-flex items-center gap-1"
                          data-testid={`link-pharma-${c.id}`}
                        >
                          {c.name}
                        </a>
                        {c.email_status === 'bounced' && <span className="text-[9px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Bounced</span>}
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-[11px] text-slate-600">
                      {c.company_profile_id ? (
                        <a
                          href={`/company-profile/${c.company_profile_id}`}
                          className="inline-flex items-center gap-1 hover:underline"
                          data-testid={`open-company-${c.id}`}
                        >
                          <Building className="w-2.5 h-2.5" />
                          {c.company_name || '—'}
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <Building className="w-2.5 h-2.5 text-slate-400" />
                          {c.company_name || <span className="text-slate-300">—</span>}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      {c.email ? (
                        <div className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                          <a href={`mailto:${c.email}`} className="hover:underline inline-flex items-center gap-1">
                            <Mail className="w-2.5 h-2.5" />{c.email}
                          </a>
                          <CopyEmail email={c.email} />
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-[11px] text-slate-500">
                      {c.designation || <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="py-2 text-[11px] text-slate-500">
                      {c.phone ? (
                        <span className="inline-flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{c.phone}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="text-right py-2 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        {c.linkedin_url && (
                          <a
                            href={c.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-[#0A66C2] hover:bg-sky-50"
                            title="Open LinkedIn profile"
                            data-testid={`linkedin-pharma-${c.id}`}
                          >
                            <Linkedin className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <a
                          href={`/pharma/${c.id}`}
                          className="text-[11px] px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
                          data-testid={`match-pharma-${c.id}`}
                        >
                          Match Assets
                        </a>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleEdit(c); }} data-testid={`edit-pharma-${c.id}`}><Pencil className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }} data-testid={`delete-pharma-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
        <Pagination page={currentPage} pages={totalPages} total={totalCount} onPageChange={handlePageChange} />
      </>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold text-slate-700">{deleteTarget?.name}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget?.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Email Dialog */}
      <Dialog open={bulkEmailOpen} onOpenChange={(open) => { if (!open && !bulkSending) { setBulkEmailOpen(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Bulk Email to Pharma Contacts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
              <p className="text-xs text-sky-700">
                This will send an email to <strong>all {totalCount} pharma contacts</strong> with valid email addresses (bounced emails will be skipped).
                The greeting will be personalized with each contact's name automatically.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={bulkSubject}
                onChange={e => setBulkSubject(e.target.value)}
                placeholder="e.g., Quick Question — Your Therapeutic Focus Areas"
                data-testid="bulk-email-subject"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Body *</Label>
              <p className="text-[10px] text-slate-400">Use [Name] for personalized greeting. e.g., "Dear [Name],"</p>
              <Textarea
                value={bulkBody}
                onChange={e => setBulkBody(e.target.value)}
                placeholder={"Dear [Name],\n\nI hope this message finds you well...\n\nWe would greatly appreciate if you could share your current therapeutic areas of interest..."}
                rows={12}
                className="text-sm font-mono"
                data-testid="bulk-email-body"
              />
            </div>

            {bulkProgress && (
              <BulkSendProgress
                progress={bulkProgress}
                testId="pharma-bulk-progress"
                onDone={() => { setBulkProgress(null); setBulkEmailOpen(false); }}
              />
            )}

            <div className="flex justify-between gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleTestEmail}
                disabled={testSending || bulkSending || !bulkSubject.trim() || !bulkBody.trim()}
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                data-testid="test-bulk-email-btn"
              >
                {testSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending Test...</> : <><Mail className="w-4 h-4 mr-2" /> Send Test to Me</>}
              </Button>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => { if (!bulkSending) { setBulkEmailOpen(false); } }} disabled={bulkSending}>
                  {bulkProgress?.status === 'completed' ? 'Close' : 'Cancel'}
                </Button>
                {(!bulkProgress || bulkProgress.status !== 'completed') && (
                  <Button
                    onClick={handleBulkEmailSend}
                    disabled={bulkSending || !bulkSubject.trim() || !bulkBody.trim()}
                    className="bg-sky-600 text-white hover:bg-sky-700"
                    data-testid="send-bulk-email-btn"
                  >
                    {bulkSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send to All Contacts</>}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
